import * as _ from 'lodash';
import { FormEvent, useState } from 'react';
import { IAmbraDirectToViewerResponse } from '@nkr/store/index';
import { IDonorProfileMedicalDetailsData, IDonorProfileMedicalDetailsLookupData } from '@nkr/models/index';
import { AboASubType, AboAbSubType } from '@nkr/enums/index';
import { Alert, Col, ControlLabel, Form, FormControl, FormGroup, InputGroup, Label, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { positiveNegativeMap, tbStatusMap } from '../models';
import YesNoUnknown from '../yes-no-unknown';
import MaskedInput from 'react-maskedinput';
import KidneyInfo from './kidney-info';
import CreatinineAlert from '../creatinine-range-alert';

interface IProps {
    isSuperAdmin: boolean,
    centerId: number,
    data: IDonorProfileMedicalDetailsData,
    lookupData: IDonorProfileMedicalDetailsLookupData,
    isInMatch: boolean,
    onSaveChange: () => void,
    onChange: (data: IDonorProfileMedicalDetailsData, checkDirty?: boolean) => void,
    onGetAmbraLink: (donor: number) => Promise<IAmbraDirectToViewerResponse>
}

const Content = (props: IProps) => {
    const [previousNumberValue, setPreviousNumberValue] = useState<{ name: string; value: string; } | null>(null);
    const [showCreatinineRangeAlert, setShowCreatinineRangeAlert] = useState(false);

    const handleSelectChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleSelectNumberChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value: null | number = null;

        if (e.currentTarget.value != null) {
            value = parseInt(e.currentTarget.value);
        }

        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleTextChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleClick = (e: FormEvent<HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        if (name == 'bloodType') {
            if (value !== 'A' && value !== 'AB') {
                data['aboASubType'] = 1;
                data['aboAbSubType'] = 1;
            }
        }

        data[name] = value;

        props.onChange(data);
    }

    const handleClickNumber = (e: FormEvent<HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = parseInt(value);

        props.onChange(data);
    }

    const handleFloatNumberChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = parseFloat(value);

        props.onChange(data);
    }

    const handleBooleanChange = (name: string, value: boolean) => {
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleNumberBooleanChange = (name: string, value: number) => {
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleNumberChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = Math.abs(parseInt(value));

        props.onChange(data);
    }

    const handleCreatinineFocus = (e: React.FocusEvent<any>) => {
        let target = e.currentTarget as HTMLInputElement;
        let data = _.cloneDeep(props.data);

        setPreviousNumberValue({
            name: target.name,
            value: data[target.name] || ''
        });
    };

    const revertCreatinine = () => {
        if (!previousNumberValue) return;
        let data = _.cloneDeep(props.data);
        data[previousNumberValue.name] = previousNumberValue.value;
        props.onChange(data, false);
    };


    const handleDateBlur = (e: React.FocusEvent<FormControl & HTMLInputElement>) => {
        let { value } = e.currentTarget;
        let numericValue = parseFloat(value);

        if (!isNaN(numericValue) && (numericValue < 80 || numericValue > 180)) {
            setShowCreatinineRangeAlert(true);
        } else {
            setShowCreatinineRangeAlert(false);
        }
    };

    const handleHeightAndWeightChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        if (name == 'heightCm') {
            data.heightCm = parseInt(value);
        }

        if (name == 'weightKg') {
            data.weightKg = parseInt(value);
        }

        props.onChange(data);
    };

    const getABOError = () => {
        
        let warningAbo = props.data.bloodType != props.data.histogeneticsAbo.replace('1', '').replace('2', '');

        if (warningAbo) {
            return (<div><div style={{ padding: 3, backgroundColor: 'yellow', color: '#333', fontSize: '87%', whiteSpace: 'nowrap' }}>The specified ABO and the ABO determined by the NKR lab do not match.</div></div>);
        }

        return "";       
    };

    return (
        <div className='container-fluid donor-profile-section donor-profile-section-medical-details'>
            <Form horizontal>
                {props.isInMatch && <Alert bsStyle='warning' bsSize='sm'>Blood Type can't be changed when the donor is in a match</Alert>}

                <FormGroup>
                    <Col xs={12} lg={5} componentClass={ControlLabel}>Date of Most Recent Evaluation:</Col>
                    <Col xs={12} lg={7}>
                        <MaskedInput
                            name='dateOfEvaluation'
                            className='form-control'
                            mask='11/11/1111'
                            placeholder='mm/dd/yyyy'
                            value={props.data.dateOfEvaluation}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col xs={12} lg={5} componentClass={ControlLabel}>
                        Is this donor medically sound with no abnormalities and all values within normal ranges?
                        <div className='text-sm' style={{ fontWeight: 'normal' }}>*If no, please describe any donor abnormalities in the medical notes box below</div>
                    </Col>
                    <Col xs={12} lg={7}>
                        <div className='form-control-static'>
                            <YesNoUnknown
                                name='isDonorMedicallySound'
                                yes={props.data.isDonorMedicallySound == true}
                                no={props.data.isDonorMedicallySound == false}
                                onChange={handleBooleanChange}
                            />
                        </div>
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col xs={12} lg={5} componentClass={ControlLabel}>
                        <div>
                            Medical Notes and Additional Tests:
                        </div>
                        <span style={{ fontWeight: 'normal' }} className='text-sm'>*if testing has been completed</span>
                    </Col>
                    <Col xs={12} lg={7}>
                        <FormControl
                            componentClass='textarea'
                            cols={20}
                            rows={8}
                            style={{ height: 80, resize: 'vertical' }}
                            name='medicalComments'
                            value={props.data.medicalComments}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col xs={12} lg={5} componentClass={ControlLabel}>24 Hour BP:</Col>
                    <Col xs={12} lg={7}>
                        <FormControl
                            componentClass='textarea'
                            cols={20}
                            rows={8}
                            style={{ height: 80, resize: 'vertical' }}
                            name='bp24Hour'
                            value={props.data.bp24Hour}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col xs={12} lg={5} componentClass={ControlLabel}>Donor CT Abnormalities:</Col>
                    <Col xs={12} lg={7}>
                        <FormControl
                            componentClass='textarea'
                            cols={20}
                            rows={8}
                            style={{ height: 80, resize: 'vertical' }}
                            name='donorCtAbnormalities'
                            value={props.data.donorCtAbnormalities}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>
            </Form>

            <Row>
                <Col xs={12} lg={6}>
                    <Form horizontal>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Blood Type:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>
                                    <label className='radio-inline'><input id='BloodTypeA' name='bloodType' type='radio' value='A' checked={props.data.bloodType == 'A'} disabled={props.isInMatch} onClick={handleClick} />A</label>
                                    <label className='radio-inline'><input id='BloodTypeB' name='bloodType' type='radio' value='B' checked={props.data.bloodType == 'B'} disabled={props.isInMatch} onClick={handleClick} />B</label>
                                    <label className='radio-inline'><input id='BloodTypeO' name='bloodType' type='radio' value='O' checked={props.data.bloodType == 'O'} disabled={props.isInMatch} onClick={handleClick} />O</label>
                                    <label className='radio-inline'><input id='BloodTypeAB' name='bloodType' type='radio' value='AB' checked={props.data.bloodType == 'AB'} disabled={props.isInMatch} onClick={handleClick} />AB</label>
                                    <label className='radio-inline'><input id='BloodTypeDNK' name='bloodType' type='radio' value='DNK' checked={props.data.bloodType == 'DNK' || props.data.bloodType == 'U'} disabled={props.isInMatch} onClick={handleClick} />Unknown</label>
                                </div>
                            </Col>
                        </FormGroup>

                        {props.data.bloodType == 'A' && (
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>A Sub Type:</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>
                                        <label className='radio-inline'><input id='ASubType1' name='aboASubType' type='radio' value='1' checked={props.data.aboASubType == AboASubType.Unknown} disabled={props.isInMatch} onClick={handleClickNumber} />Unknown</label>
                                        <label className='radio-inline'><input id='ASubType2' name='aboASubType' type='radio' value='2' checked={props.data.aboASubType == AboASubType.A1} disabled={props.isInMatch} onClick={handleClickNumber} />A1</label>
                                        <label className='radio-inline'><input id='ASubType3' name='aboASubType' type='radio' value='3' checked={props.data.aboASubType == AboASubType.A2} disabled={props.isInMatch} onClick={handleClickNumber} />A2</label>
                                    </div>
                                </Col>
                            </FormGroup>
                        )}

                        {props.data.bloodType == 'AB' && (
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>AB Sub Type:</Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>
                                        <label className='radio-inline'><input id='ABSubType1' name='aboAbSubType' type='radio' value='1' checked={props.data.aboAbSubType == AboAbSubType.Unknown} disabled={props.isInMatch} onClick={handleClickNumber} />Unknown</label>
                                        <label className='radio-inline'><input id='ABSubType2' name='aboAbSubType' type='radio' value='2' checked={props.data.aboAbSubType == AboAbSubType.A1b} disabled={props.isInMatch} onClick={handleClickNumber} />A1B</label>
                                        <label className='radio-inline'><input id='ABSubType3' name='aboAbSubType' type='radio' value='3' checked={props.data.aboAbSubType == AboAbSubType.A2b} disabled={props.isInMatch} onClick={handleClickNumber} />A2B</label>
                                    </div>
                                </Col>
                            </FormGroup>
                        )}

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>ABO Determined by NKR Lab?</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static' style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 10 }}><Label style={{ fontSize: '100%' }}>{props.data.histogeneticsAbo}</Label></div>
                                    { getABOError() }
                                </div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Is ABO Accuracy Confirmed?</Col>
                            <Col xs={12} lg={7}>
                                <YesNoUnknown
                                    name='isAboConfirmed'
                                    yes={props.data.isAboConfirmed}
                                    no={!props.data.isAboConfirmed}
                                    onChange={handleBooleanChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Date of Birth:</Col>
                            <Col xs={12} lg={7}>
                                <MaskedInput
                                    name='birthDate'
                                    className='form-control'
                                    mask='11/11/1111'
                                    placeholder='mm/dd/yyyy'
                                    value={props.data.birthDate}
                                    onChange={handleTextChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Height:&nbsp;</Col>
                            <Col xs={12} lg={7}>
                                <InputGroup>
                                    <InputGroup.Addon>cm</InputGroup.Addon>
                                    <FormControl
                                        type='number'
                                        name='heightCm'
                                        maxLength={3}
                                        defaultValue={props.data.heightCm}
                                        onChange={handleHeightAndWeightChange}
                                    />
                                    <InputGroup.Addon>
                                        {props.data.heightFeet}' {props.data.heightInchesRemaining}''
                                    </InputGroup.Addon>
                                </InputGroup>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Weight:&nbsp;</Col>
                            <Col xs={12} lg={7}>
                                <InputGroup>
                                    <InputGroup.Addon>
                                        kgs
                                    </InputGroup.Addon>
                                    <FormControl
                                        type='number'
                                        name='weightKg'
                                        maxLength={5}
                                        value={props.data.weightKg}
                                        onChange={handleHeightAndWeightChange}
                                    />
                                    <InputGroup.Addon>
                                        {props.data.weightLbs} lbs.
                                    </InputGroup.Addon>
                                </InputGroup>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Blood Pressure:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='bloodPressure'
                                    value={props.data.bloodPressure}
                                    onChange={handleSelectNumberChange}
                                >
                                    {props.lookupData.bloodPressureList.map(item =>
                                        <option key={'da-' + item.id} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Systolic BP/Diastolic BP:</Col>
                            <Col xs={12} lg={7}>
                                <InputGroup>
                                    <FormControl
                                        id='SBP'
                                        type='number'
                                        name='sbp'
                                        maxLength={4}
                                        value={props.data.sbp}
                                        onChange={handleNumberChange}
                                        className='form-control'
                                    />
                                    <InputGroup.Addon>/</InputGroup.Addon>
                                    <FormControl
                                        id='DBP'
                                        type='number'
                                        name='dbp'
                                        maxLength={4}
                                        value={props.data.dbp}
                                        onChange={handleNumberChange}
                                        className='form-control'
                                    />
                                </InputGroup>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Race:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='race'
                                    value={props.data.race}
                                    onChange={handleSelectChange}
                                >
                                    <option value={''}>- Select -</option>
                                    {props.lookupData.raceList.map(item =>
                                        <option key={'da-' + item.id} value={item.abbreviation}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Gender:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='gender'
                                    value={props.data.gender}
                                    onChange={handleSelectChange}
                                >
                                    <option value={''}>- Select -</option>
                                    {props.lookupData.genderList.map(item =>
                                        <option key={'da-' + item.id} value={item.abbreviation}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Creatinine Clearance ml/min:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    id='CreatinineClearance'
                                    name='creatinineClearance'
                                    type='number'
                                    value={props.data.creatinineClearance}
                                    onChange={handleNumberChange}
                                    onFocus={handleCreatinineFocus}
                                    onBlur={handleDateBlur}
                                    className='form-control'
                                />
                                <span className='text-sm pull-right text-right'>This should only be entered as absolute CrCl in ml/min, and NOT normalized CrCl in ml/min/1.73m2.</span>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Creatinine Clearance Collection Method:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='creatinineCollectionMethod'
                                    value={props.data.creatinineCollectionMethod}
                                    onChange={handleSelectChange}
                                >
                                    {props.lookupData.collectionMethodList.map(item =>
                                        <option key={'da-' + item.id} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Serum Creatinine:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    id='SerumCreatinine'
                                    type='number'
                                    name='serumCreatinine'
                                    value={props.data.serumCreatinine}
                                    onChange={handleFloatNumberChange}
                                    className='form-control'
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Albumin Excretion:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    id='AlbuminExcretion'
                                    name='albuminExcretion'
                                    value={props.data.albuminExcretion}
                                    onChange={handleTextChange}
                                    className='form-control'
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>24 Hour Urine Protein:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    id='UrineProtein'
                                    name='urineProtein'
                                    value={props.data.urineProtein}
                                    onChange={handleTextChange}
                                    className='form-control'
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Hepatitis B Antigen:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='hepBAntigen'
                                    value={props.data.hepBAntigen}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'hep-b-antigen-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Hepatitis B Core Antibody:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='hepBAntibody'
                                    value={props.data.hepBAntibody}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'hep-b-antibody-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>RPR/VDRL:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='rprVdrl'
                                    value={props.data.rprVdrl}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'rpr-vdrl-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                    </Form>
                </Col>
                <Col xs={12} lg={6}>
                    <Form horizontal>
                        <KidneyInfo
                            data={props.data}
                            whichKidney={props.data.whichKidney}
                            kidneyHeight={props.data.kidneyHeight}
                            kidneyLength={props.data.kidneyLength}
                            kidneyWidth={props.data.kidneyWidth}
                            kidneyVolume={props.data.kidneyVolume}
                            numberOfVeins={props.data.numberOfVeins}
                            numberOfArteries={props.data.numberOfArteries}
                            numberOfUreters={props.data.numberOfUreters}
                            hasEarlyBifurcation={props.data.hasEarlyBifurcation}
                            earlyBifurcationLength={props.data.earlyBifurcationLength}
                            whichKidneyList={props.lookupData.whichKidneyList}
                            onChange={props.onChange}
                        />

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>History of At Risk activity?</Col>
                            <Col xs={12} lg={7}>
                                <YesNoUnknown
                                    name='isAtRisk'
                                    yes={props.data.isAtRisk == 1}
                                    no={props.data.isAtRisk == 0}
                                    unknown={props.data.isAtRisk == null}
                                    onChangeNumber={handleNumberBooleanChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>EBV (IGG):</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='ebv'
                                    value={props.data.ebv}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'ebv-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>CMV (IGG):</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='cmv'
                                    value={props.data.cmv}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'cmv-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Anti-HCV:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='antiHcv'
                                    value={props.data.antiHcv}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'anti-hcv-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>HIV:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='hiv'
                                    value={props.data.hiv}
                                    onChange={handleTextChange}
                                >
                                    {positiveNegativeMap.map(item =>
                                        <option key={'hiv-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>TB Status:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='tbStatus'
                                    value={props.data.tbStatus}
                                    onChange={handleTextChange}
                                >
                                    {tbStatusMap.map(item =>
                                        <option key={'tbStatus-' + item.value} value={item.value}>{item.description}</option>
                                    )}
                                </FormControl>
                                <span className='text-sm text-right pull-right'>*If active or latent, please note treatment status in the medical comments.</span>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Has history of smoking?</Col>
                            <Col xs={12} lg={7}>
                                <YesNoUnknown
                                    name='hasHistoryOfSmoking'
                                    yes={props.data.hasHistoryOfSmoking == true}
                                    no={props.data.hasHistoryOfSmoking == false}
                                    unknown={props.data.hasHistoryOfSmoking == null}
                                    onChange={handleBooleanChange}
                                />
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>
                                Has Nutcracker Syndrome?&nbsp;
                                <OverlayTrigger trigger={['hover', 'click']} placement='right'
                                    overlay={<Popover>Nutcracker syndrome is a rare vascular condition characterized by compression of the left renal vein between the aorta and the superior mesenteric artery causing symptoms. When this anatomical finding does not produce symptoms, it is referred to as Nutcracker Phenomenon.</Popover>}>
                                    <span className='glyphicon glyphicon-info-sign'></span>
                                </OverlayTrigger>
                            </Col>
                            <Col xs={12} lg={7}>
                                <YesNoUnknown
                                    name='hasNutcracker'
                                    yes={props.data.hasNutcracker == 1}
                                    no={props.data.hasNutcracker == 0}
                                    unknown={props.data.hasNutcracker == -1}
                                    onChangeNumber={(name, value) => handleNumberBooleanChange(name, value ?? -1)}
                                />
                            </Col>
                        </FormGroup>
                    </Form>
                </Col>
            </Row>

            <CreatinineAlert
                show={showCreatinineRangeAlert}
                onConfirm={() => {
                    setShowCreatinineRangeAlert(false);
                    props.onSaveChange();
                }}
                onClose={() => {
                    setShowCreatinineRangeAlert(false);
                    revertCreatinine();
                }}
            />
        </div>
    );
}

export default Content;

